import React, {
  useRef,
  useEffect,
  useState,
  useMemo,
  useCallback,
  useId,
} from 'react';
import { Device } from '@twilio/voice-sdk';
import { Box, Button, Text, Paragraph, Heading } from '@twilio-paste/core';
import { TimeIcon } from '@twilio-paste/icons/esm/TimeIcon';
import { LogoTwilioIcon } from '@twilio-paste/icons/esm/LogoTwilioIcon';
import { CallIcon } from '@twilio-paste/icons/esm/CallIcon';
import { MicrophoneOnIcon } from '@twilio-paste/icons/esm/MicrophoneOnIcon';
import { MicrophoneOffIcon } from '@twilio-paste/icons/esm/MicrophoneOffIcon';
import { ArrowUpIcon } from '@twilio-paste/icons/esm/ArrowUpIcon';
import { sendAnalyticsEvent } from '../utils/analytics';
import { translation } from '../utils/translation';
import { formatDuration } from '../utils/formatDuration';
import { sanitizeMessage } from '../utils/sanitizeMessage';

const renderWaitingMessage = (language) => {
  return (
    <Box display="flex" columnGap="space40">
      <Box>
        <LogoTwilioIcon decorative={false} title="Twilio" color="#DB132A" />
      </Box>
      <Text
        as="p"
        color="#fff"
        fontSize="fontSize30"
        fontWeight="fontWeightBold"
      >
        {translation('loading', language)}
      </Text>
    </Box>
  );
};

const renderIntroContent = (
  isWebRTCInCall,
  isWebRTCRegistered,
  handleStart,
  language
) => {
  return (
    <Box
      display="flex"
      flexDirection="column"
      flex="1"
      justifyContent="center"
      alignItems="center"
      padding="space60"
    >
      <Box
        maxWidth="512px"
        display="flex"
        flexDirection="column"
        rowGap="space80"
      >
        <Box display="flex" alignItems="center" id="cai-voice-icon">
          <Box
            style={{
              backgroundColor: '#000D25',
              padding: '10px',
              borderRadius: '50%',
              height: '72px',
              width: '72px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              as="img"
              src="/voice-icon.svg"
              alt="Voice icon"
              width="47px"
              height="47px"
            />
          </Box>
        </Box>

        <Box>
          <Heading as="h2" marginBottom="space0">
            <Box
              style={{
                textTransform: 'uppercase',
                color: '#fff',
                marginBottom: '12px',
              }}
              className="buffalo-font heading"
            >
              {translation('seeConversationalAIInActionSee', language)}{' '}
              <Box
                as="span"
                style={{ color: '#EF223A' }}
                className="buffalo-font heading"
              >
                {translation(
                  'seeConversationalAIInActionConversationalAI',
                  language
                )}
              </Box>{' '}
              {translation('seeConversationalAIInActionInAction', language)}
            </Box>
          </Heading>
          <Paragraph marginBottom="space0">
            <Box as="span" style={{ color: '#fff' }} className="small-font">
              {translation('introMessage', language)}
            </Box>
            <Box as="span" style={{ color: '#fff' }} className="small-font">
              *
            </Box>
          </Paragraph>
        </Box>
        <Box display="flex" justifyContent="flex-start">
          <Button
            variant="primary"
            disabled={isWebRTCInCall || !isWebRTCRegistered}
            onClick={handleStart}
            size="default"
            className="red-owl-btn primary"
          >
            {translation('letsChat', language)}
            <MicrophoneOnIcon
              decorative={false}
              title={translation('letsChat', language)}
            />
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

const renderErrorMessage = (message) => {
  return (
    <Box display="flex" justifyContent="flex-end" marginY="space40">
      <Box
        minWidth="size20"
        display="flex"
        alignItems="center"
        columnGap="space30"
        width="100%"
      >
        <Box
          padding="space40"
          backgroundColor="#EF223A"
          borderRadius="8px"
          width="100%"
        >
          <Text
            as="p"
            lineHeight="lineHeight15"
            color="#fff"
            textAlign="center"
            fontSize="fontSize30"
            fontWeight="500"
          >
            {message}
          </Text>
        </Box>
      </Box>
    </Box>
  );
};

const renderWebRTCControls = (
  isWebRTCInCall,
  isWebRTCRegistered,
  makeCall,
  hangUpCall,
  formatDuration,
  callDuration,
  isMuted,
  toggleMute,
  isInitialMute,
  language
) => {
  return (
    <Box
      position="absolute"
      bottom="20px"
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="100%"
      left="0"
    >
      <Box
        justifyContent="space-between"
        alignItems="center"
        paddingX="space40"
        paddingY="space40"
        display="flex"
        columnGap="space40"
      >
        {/* WebRTC Call Controls */}

        {!isWebRTCInCall ? (
          <Button
            variant="primary"
            disabled={isWebRTCInCall || !isWebRTCRegistered}
            onClick={makeCall}
            size="small"
            className="red-owl-btn primary"
          >
            <CallIcon decorative={false} title="Start Call" />
            {translation('startCall', language)}
          </Button>
        ) : null}

        {isWebRTCInCall ? (
          <>
            <Button
              disabled={!isWebRTCInCall || isInitialMute}
              onClick={toggleMute}
              size="small"
              className="red-owl-btn secondary"
            >
              {isMuted && !isInitialMute ? (
                <MicrophoneOnIcon
                  decorative={false}
                  title={translation('mute', language)}
                />
              ) : (
                <MicrophoneOffIcon
                  decorative={false}
                  title={translation('unmute', language)}
                />
              )}
              {isMuted && !isInitialMute
                ? translation('unmute', language)
                : translation('mute', language)}
            </Button>
            <Button
              variant="destructive"
              disabled={!isWebRTCInCall}
              onClick={hangUpCall}
              size="small"
              className="red-owl-btn destructive"
            >
              <CallIcon
                decorative={false}
                title={translation('endCall', language)}
              />
              {translation('endCall', language)}
            </Button>
          </>
        ) : null}

        {isWebRTCInCall && (
          <Box
            alignItems="center"
            justifyContent="center"
            columnGap="space20"
            display="flex"
            flexDirection="row"
          >
            <TimeIcon
              decorative={false}
              title={translation('callDuration', language)}
              color="#fff"
            />
            <Text
              fontSize="fontSize30"
              fontWeight="fontWeightMedium"
              marginTop="space5"
              color="#fff"
            >
              {formatDuration(callDuration)}
            </Text>
          </Box>
        )}
      </Box>
    </Box>
  );
};

const renderMainConversationArea = (
  messages,
  messageRefs,
  conversationRef,
  filteredMessages,
  tokenExpired,
  language
) => {
  const conversationRelayUrl =
    'https://www.twilio.com/en-us/products/conversational-ai/conversationrelay?utm_campaign=agentproductivity&utm_source=cai-report-agent&utm_medium=referral';
  return (
    <Box padding="space0" height="100%" display="flex" flexDirection="column">
      <Box
        padding="space30"
        paddingBottom="space30"
        display="flex"
        zIndex="5"
        justifyContent="center"
      >
        <Text
          as="h2"
          fontSize="fontSize40"
          padding="space20"
          color="#fff"
          fontWeight="fontWeightLight"
          textAlign="center"
        >
          {translation('poweredBy', language)}{' '}
          <Box
            as="a"
            style={{
              cursor: 'pointer',
              textDecoration: 'underline',
              position: 'relative',
            }}
            href={conversationRelayUrl}
            onClick={(e) => {
              e.preventDefault();
              sendAnalyticsEvent('Outbound CR Link');
              window.open(conversationRelayUrl, '_blank');
            }}
          >
            {translation('conversationRelay', language)}
            <Box
              as="span"
              style={{
                position: 'absolute',
                right: '-18px',
                top: '-2px',
                transform: 'rotate(45deg)',
              }}
            >
              <ArrowUpIcon
                size="sizeIcon20"
                decorative={false}
                title="Learn more about ConversationRelay"
              />
            </Box>
          </Box>
        </Text>
      </Box>

      <Box
        ref={conversationRef}
        id="conversationContainer"
        flex="1"
        overflowY="scroll"
        paddingX="space50"
        paddingRight="space50"
        paddingBottom="space50"
        height="80vh"
        display="flex"
        flexDirection="column"
      >
        <Box
          orientation="vertical"
          spacing="space40"
          display="flex"
          flexDirection="column"
          flexGrow={1}
          marginBottom="space150"
        >
          {messages.length === 0 ? (
            renderWaitingMessage(language)
          ) : (
            <>
              {filteredMessages.map((message) => {
                const isCustomer = message.sender === 'Customer';

                // Find the original index for ref assignment
                const originalIndex = messages.findIndex(
                  (msg) => msg === message
                );

                return (
                  <Box
                    key={originalIndex}
                    ref={(el) => {
                      if (el) {
                        messageRefs.current[originalIndex] = el;
                      }
                    }}
                    maxWidth="size40"
                    display="flex"
                    marginLeft={isCustomer ? 'auto' : '0'}
                    justifyContent={isCustomer ? 'flex-end' : 'flex-start'}
                    marginY="space40"
                  >
                    <Box
                      minWidth="size20"
                      display="flex"
                      alignItems="flex-end"
                      columnGap="space30"
                    >
                      {!isCustomer ? (
                        <Box style={{ paddingRight: 'space20' }}>
                          <LogoTwilioIcon
                            decorative={false}
                            title="Twilio"
                            color="#DB132A"
                          />
                        </Box>
                      ) : null}
                      <Box
                        padding="space40"
                        backgroundColor={isCustomer ? '#38425E' : '#fff'}
                        borderRadius="8px"
                        borderBottomLeftRadius={isCustomer ? '8px' : '0'}
                        borderBottomRightRadius={isCustomer ? '0' : '8px'}
                      >
                        <Text
                          as="p"
                          fontSize="fontSize30"
                          lineHeight="lineHeight15"
                          fontWeight="500"
                          color={
                            isCustomer ? 'colorTextBrandInverse' : 'colorText'
                          }
                        >
                          {sanitizeMessage(message.content)}
                        </Text>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
              {tokenExpired &&
                renderErrorMessage(translation('tokenExpired', language))}
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const ConversationArea = ({
  messages,
  onDeviceIdentity,
  onCallStart,
  onCallEnd,
  language,
}) => {
  const conversationRef = useRef(null);
  const messageRefs = useRef({});
  const uuid = useId();

  // Show conversation area after intro is dismissed
  const [showConversation, setShowConversation] = useState(false);

  // WebRTC state
  const [isWebRTCRegistered, setIsWebRTCRegistered] = useState(false);
  const [isWebRTCInCall, setIsWebRTCInCall] = useState(false);
  const [isWebRTCInitialized, setIsWebRTCInitialized] = useState(false);
  const [webRTCInitializationFailed, setWebRTCInitializationFailed] =
    useState(false);
  const [isWebRTCInitializing, setIsWebRTCInitializing] = useState(false);
  const [tokenExpired, setTokenExpired] = useState(false);

  const [callDuration, setCallDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [isInitialMute, setIsInitialMute] = useState(false);

  const webRTCDeviceRef = useRef(null);
  const webRTCCallRef = useRef(null);
  const callDurationIntervalRef = useRef(null);
  const initialMuteTimeoutRef = useRef(null);
  const initializationStartedRef = useRef(false);
  const deviceRegisteredRef = useRef(false);

  // Memoize filtered messages to avoid recalculating on every render
  const filteredMessages = useMemo(() => {
    return messages.filter((message, index) => {
      const isCustomer = message.sender === 'Customer';
      const isAI = message.sender === 'Conversation Relay Assistant';

      // Skip first message, non-customer/AI messages, or duplicate consecutive messages
      return (
        index > 0 &&
        (isCustomer || isAI) &&
        (index === messages.length - 1 ||
          messages[index - 1].content !== message.content)
      );
    });
  }, [messages]);

  // Call duration timer functions
  const startCallDurationTimer = useCallback(() => {
    setCallDuration(0);
    callDurationIntervalRef.current = setInterval(() => {
      setCallDuration((prev) => prev + 1);
    }, 1000);
  }, []);

  const stopCallDurationTimer = useCallback(() => {
    if (callDurationIntervalRef.current) {
      clearInterval(callDurationIntervalRef.current);
      callDurationIntervalRef.current = null;
    }
  }, []);

  // Helper function to clear initial mute timeout
  const clearInitialMuteTimeout = useCallback(() => {
    if (initialMuteTimeoutRef.current) {
      clearTimeout(initialMuteTimeoutRef.current);
      initialMuteTimeoutRef.current = null;
    }
  }, []);

  // Helper function to reset call state
  const resetCallState = useCallback(() => {
    setIsWebRTCInCall(false);
    webRTCCallRef.current = null;
    stopCallDurationTimer();
    setIsMuted(false);
    setIsInitialMute(false);
    clearInitialMuteTimeout();
  }, [stopCallDurationTimer, clearInitialMuteTimeout]);

  // Auto-scroll to newest message when new messages are added
  useEffect(() => {
    if (conversationRef.current && filteredMessages.length > 0) {
      // Use requestAnimationFrame for better performance
      requestAnimationFrame(() => {
        if (conversationRef.current) {
          // Get the last filtered message index
          const lastMessageIndex =
            filteredMessages[filteredMessages.length - 1];
          const originalIndex = messages.findIndex(
            (msg) => msg === lastMessageIndex
          );

          // If we found a valid message, scroll to it
          if (originalIndex !== -1 && messageRefs.current[originalIndex]) {
            const messageElement = messageRefs.current[originalIndex];

            // Use scrollIntoView for better performance and simpler logic
            messageElement.scrollIntoView({
              behavior: 'smooth',
              block: 'start', // This ensures we scroll to the top of the message
              inline: 'nearest',
            });
          }
        }
      });
    }
  }, [filteredMessages, messages]);

  // Initialize WebRTC Device
  const initializeWebRTCDevice = useCallback(async () => {
    // Prevent duplicate initialization
    if (initializationStartedRef.current) {
      return;
    }
    initializationStartedRef.current = true;

    setIsWebRTCInitializing(true);

    try {
      // Clean up any existing device first
      if (webRTCDeviceRef.current) {
        try {
          webRTCDeviceRef.current.destroy();
        } catch (error) {
          console.error('Error destroying WebRTC device:', error);
        }
        webRTCDeviceRef.current = null;
      }

      // Always generate a new identity on page load
      const identity = `user_${
        Date.now().toString() +
        Math.floor(Math.random() * 10000000 + 1).toString() +
        uuid
      }`;

      const response = await fetch('/api/twilio/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          identity, // Unique identity for this session
        }),
      });

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ error: 'Unknown error' }));
        console.error('Token fetch failed:', errorData);
        throw new Error(errorData.error ?? `HTTP ${response.status}`);
      }

      const { token } = await response.json();

      // Create new Device instance with options to reduce Insights verbosity
      const device = new Device(token, {
        logLevel: 'warn', // Only show warnings and errors, not debug/verbose
      });

      // Set up event listeners
      device.on('registered', () => {
        // Prevent duplicate registration calls
        if (deviceRegisteredRef.current) {
          return;
        }
        deviceRegisteredRef.current = true;

        setIsWebRTCRegistered(true);
        setIsWebRTCInitializing(false);

        // Call the callback to update URL and filter with new identity
        if (onDeviceIdentity && device.identity) {
          onDeviceIdentity(device.identity);
        }
      });

      device.on('unregistered', () => {
        setIsWebRTCRegistered(false);
      });

      device.on('error', (error) => {
        setIsWebRTCInitializing(false);
        // Check for AccessTokenExpired error (20104)
        if (
          error.code === 20104 ||
          error.message.includes('AccessTokenExpired')
        ) {
          setTokenExpired(true);
          setIsWebRTCRegistered(false);
          setIsWebRTCInCall(false);
          setIsWebRTCInitialized(false);

          // Disconnect the call if it exists
          if (webRTCCallRef.current) {
            webRTCCallRef.current.disconnect();
            webRTCCallRef.current = null;
          }

          resetCallState();
        }
        // If device gets destroyed, mark as failed
        if (error.message.includes('Device has been destroyed')) {
          setWebRTCInitializationFailed(true);
          setIsWebRTCRegistered(false);
        }
      });

      webRTCDeviceRef.current = device;
      setIsWebRTCInitialized(true);

      // Register the device
      device.register();
    } catch (error) {
      console.error('Error initializing WebRTC device:', error);
      setIsWebRTCInitializing(false);
      setWebRTCInitializationFailed(true);
    }
  }, [uuid, onDeviceIdentity, resetCallState]);

  // Initialize WebRTC Device
  useEffect(() => {
    // Prevent multiple initialization attempts
    if (
      isWebRTCInitialized ||
      webRTCInitializationFailed ||
      isWebRTCInitializing ||
      initializationStartedRef.current
    ) {
      return;
    }

    initializeWebRTCDevice();

    // Cleanup on unmount
    return () => {
      if (webRTCDeviceRef.current) {
        try {
          webRTCDeviceRef.current.destroy();
        } catch (error) {
          console.error('Error destroying WebRTC device:', error);
        }
        webRTCDeviceRef.current = null;
      }
      stopCallDurationTimer();
      clearInitialMuteTimeout();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isWebRTCInitialized,
    webRTCInitializationFailed,
    isWebRTCInitializing,
    initializeWebRTCDevice,
  ]);

  // Handle start - show conversation and make call
  const handleStart = () => {
    setShowConversation(true);
    makeCall();
  };

  // Make outbound call to TwiML App
  const makeCall = async () => {
    if (!webRTCDeviceRef.current) {
      return;
    }

    try {
      // Reset mute state when starting a new call
      setIsMuted(false);
      setIsInitialMute(false);
      clearInitialMuteTimeout();

      const call = await webRTCDeviceRef.current.connect({
        params: {
          Language: language,
        },
      });

      webRTCCallRef.current = call;
      setIsWebRTCInCall(true);

      // Set up call event listeners
      call.on('accept', () => {
        startCallDurationTimer();
        onCallStart();

        sendAnalyticsEvent('Call Started');

        // Mute microphone for 3000ms to prevent picking up speaker audio
        // This is an internal mute that shouldn't affect the UI
        if (call.mute) {
          call.mute(true);
          setIsInitialMute(true); // Track that we're in initial mute period

          clearInitialMuteTimeout();

          // Unmute after 3000ms
          initialMuteTimeoutRef.current = setTimeout(() => {
            if (call && call.mute) {
              call.mute(false);
              setIsInitialMute(false); // Initial mute period is over
              setIsMuted(false); // Update state to reflect unmuted status
            }
          }, 3000);
        }
      });

      call.on('disconnect', () => {
        resetCallState();
        onCallEnd();
        sendAnalyticsEvent('Call Ended');
      });

      call.on('cancel', () => {
        resetCallState();
        onCallEnd();
        sendAnalyticsEvent('Call Cancelled');
      });

      call.on('reject', () => {
        resetCallState();
        onCallEnd();
        sendAnalyticsEvent('Call Rejected');
      });
    } catch (error) {
      console.error('Error making call:', error);
    }
  };

  // Hang up call
  const hangUpCall = () => {
    if (webRTCCallRef.current) {
      webRTCCallRef.current.disconnect();
    }
  };

  // Toggle mute (only allowed after initial mute period)
  const toggleMute = () => {
    // Don't allow toggling during initial mute period
    if (isInitialMute) {
      return;
    }

    if (webRTCCallRef.current && webRTCCallRef.current.mute) {
      const newMutedState = !isMuted;
      webRTCCallRef.current.mute(newMutedState);
      setIsMuted(newMutedState);

      sendAnalyticsEvent(`Call ${newMutedState ? 'Muted' : 'Unmuted'}`);
    }
  };

  if (webRTCInitializationFailed) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        flex="1"
        justifyContent="center"
        alignItems="center"
      >
        <Box
          flex="1"
          padding="space30"
          overflow="hidden"
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          {renderErrorMessage(
            translation('webRTCInitializationFailed', language)
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" flex="1">
      {/* Show intro content or conversation area */}
      {!showConversation ? (
        renderIntroContent(
          isWebRTCInCall,
          isWebRTCRegistered,
          handleStart,
          language
        )
      ) : (
        <Box flex="1" padding="space30" overflow="hidden">
          {renderMainConversationArea(
            messages,
            messageRefs,
            conversationRef,
            filteredMessages,
            tokenExpired,
            language
          )}

          {tokenExpired
            ? null
            : renderWebRTCControls(
                isWebRTCInCall,
                isWebRTCRegistered,
                makeCall,
                hangUpCall,
                formatDuration,
                callDuration,
                isMuted,
                toggleMute,
                isInitialMute,
                language
              )}
        </Box>
      )}
    </Box>
  );
};

export default ConversationArea;
